import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL } from '../config/api';
import '../styles/BatchProposal.css';

interface Collaboration {
  _id: string;
  handle: string;
  email: string;
  platform: string;
  agreedPrice: number;
  postMetrics?: {
    primaryViews?: number;
    cpm?: number;
  };
  totalViews?: number;
}

interface BatchData {
  _id: string;
  handle: string;
  primaryPlatform: string;
  averageCollaborationViews: number;
  lastCollaborationViews: number;
  batchPrice: number;
  batchSize: number;
  batchCount: number;
  status: 'pending' | 'accepted' | 'negotiating' | 'rejected';
  negotiatedPrice?: number;
  collaborationId: Collaboration;
  createdAt: string;
}

const BatchProposal: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const [batch, setBatch] = useState<BatchData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNegotiationModal, setShowNegotiationModal] = useState(false);
  const [negotiatedPrice, setNegotiatedPrice] = useState<string>('');
  const [negotiatedPackageSize, setNegotiatedPackageSize] = useState<string>('4');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseStatus, setResponseStatus] = useState<'accepted' | 'negotiating' | 'rejected' | null>(null);

  useEffect(() => {
    fetchBatchProposal();
  }, [batchId]);

  const fetchBatchProposal = async () => {
    try {
      const response = await fetch(`${API_URL}/api/batch/proposal/${batchId}`);
      const result = await response.json();
      
      if (result.success) {
        setBatch(result.data);
        if (result.data.status !== 'pending') {
          setResponseStatus(result.data.status);
        }
      } else {
        setError(result.error || 'Failed to load proposal');
      }
    } catch (err) {
      setError('Failed to load proposal');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/batch/proposal/${batchId}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setResponseStatus('accepted');
      } else {
        alert(result.error || 'Failed to accept proposal');
      }
    } catch (err) {
      alert('Failed to accept proposal');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDecline = () => {
    setShowNegotiationModal(true);
  };

  const handleNegotiationSubmit = async () => {
    if (!negotiatedPrice || parseFloat(negotiatedPrice) < 0) {
      alert('Please enter a valid price');
      return;
    }


    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/batch/proposal/${batchId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          negotiatedPrice: parseFloat(negotiatedPrice),
          negotiatedPackageSize: parseInt(negotiatedPackageSize) || 4
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setResponseStatus('negotiating');
        setShowNegotiationModal(false);
      } else {
        alert(result.error || 'Failed to submit counter offer');
      }
    } catch (err) {
      alert('Failed to submit counter offer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleJustDecline = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/batch/proposal/${batchId}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      
      const result = await response.json();
      
      if (result.success) {
        setResponseStatus('rejected');
        setShowNegotiationModal(false);
      } else {
        alert(result.error || 'Failed to decline proposal');
      }
    } catch (err) {
      alert('Failed to decline proposal');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString('en-US');
  };

  if (loading) {
    return (
      <div className="batch-proposal-container">
        <div className="loading">Loading proposal...</div>
      </div>
    );
  }

  if (error || !batch) {
    return (
      <div className="batch-proposal-container">
        <div className="error-message">
          <h2>Proposal Not Found</h2>
          <p>{error || 'This proposal link may be invalid or expired.'}</p>
        </div>
      </div>
    );
  }

  // Show response status if already responded
  if (responseStatus) {
    return (
      <div className="batch-proposal-container">
        <div className="response-complete">
          <div className="response-icon">
            {responseStatus === 'accepted' && '✅'}
            {responseStatus === 'negotiating' && '💬'}
            {responseStatus === 'rejected' && '👋'}
          </div>
          <h2>
            {responseStatus === 'accepted' && 'Thank You!'}
            {responseStatus === 'negotiating' && 'Counter Offer Received'}
            {responseStatus === 'rejected' && 'Proposal Declined'}
          </h2>
          <p>
            {responseStatus === 'accepted' && "We'll send you the details soon."}
            {responseStatus === 'negotiating' && "Thank you for your counter offer. We'll review and get back to you."}
            {responseStatus === 'rejected' && 'Thank you for your response. We look forward to future opportunities.'}
          </p>
        </div>
      </div>
    );
  }

  const cpm = batch.collaborationId.postMetrics?.cpm || 0;
  const totalPackageValue = batch.batchPrice * batch.batchSize;
  const originalPrice = batch.collaborationId.agreedPrice;
  const discount = Math.round(((originalPrice - batch.batchPrice) / originalPrice) * 100);

  return (
    <div className="batch-proposal-container">
      <div className="proposal-card">
        {/* Header */}
        <div className="proposal-header">
          <h1>Long-term Collaboration Proposal</h1>
          <p className="subtitle">Thank you for your successful collaboration</p>
        </div>

        {/* Creator Info */}
        <div className="creator-section">
          <h2>{batch.handle}</h2>
          <span className="platform-badge">{batch.primaryPlatform}</span>
        </div>

        {/* Performance Section */}
        <div className="performance-section">
          <h3>Previous Collaboration Performance</h3>
          <div className="metrics-grid">
            <div className="metric-card">
              <span className="metric-label">Average Views</span>
              <span className="metric-value">{formatNumber(batch.averageCollaborationViews)}</span>
            </div>
            <div className="metric-card">
              <span className="metric-label">Last Collaboration Views</span>
              <span className="metric-value">{formatNumber(batch.lastCollaborationViews)}</span>
            </div>
            {cpm > 0 && (
              <div className="metric-card">
                <span className="metric-label">CPM</span>
                <span className="metric-value">${cpm.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Proposal Details */}
        <div className="proposal-details">
          <h3>Proposal Details</h3>
          <div className="details-list">
            <div className="detail-row">
              <span className="detail-label">Batch Size</span>
              <span className="detail-value">{batch.batchSize} videos</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Price per Video</span>
              <span className="detail-value">
                ${batch.batchPrice}
                <span className="discount-badge">{discount}% off</span>
              </span>
            </div>
            <div className="detail-row total">
              <span className="detail-label">Total Package Value</span>
              <span className="detail-value">${totalPackageValue}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button 
            className="btn-accept"
            onClick={handleAccept}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : 'Accept'}
          </button>
          <button 
            className="btn-decline"
            onClick={handleDecline}
            disabled={isSubmitting}
          >
            Decline
          </button>
        </div>
      </div>

      {/* Negotiation Modal */}
      {showNegotiationModal && (
        <div className="modal-overlay" onClick={() => setShowNegotiationModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Would you like to suggest a different price?</h3>
            <p>Enter your preferred price per video and package size</p>
            
            <div className="negotiation-inputs">
              <div className="input-group">
                <label>Price per video</label>
                <div className="price-input-group">
                  <span className="currency">$</span>
                  <input
                    type="number"
                    value={negotiatedPrice}
                    onChange={(e) => setNegotiatedPrice(e.target.value)}
                    placeholder="0"
                    min="0"
                    step="1"
                  />
                </div>
              </div>
              <div className="input-group">
                <label>Package size</label>
                <div className="size-input-group">
                  <input
                    type="number"
                    value={negotiatedPackageSize}
                    onChange={(e) => setNegotiatedPackageSize(e.target.value)}
                    placeholder="4"
                    min="1"
                    step="1"
                  />
                  <span className="unit">videos</span>
                </div>
              </div>
            </div>
            
            <div className="modal-buttons">
              <button 
                className="btn-submit-offer"
                onClick={handleNegotiationSubmit}
                disabled={isSubmitting || !negotiatedPrice}
              >
                Submit Counter Offer
              </button>
              <button 
                className="btn-just-decline"
                onClick={handleJustDecline}
                disabled={isSubmitting}
              >
                Just Decline
              </button>
              <button 
                className="btn-cancel"
                onClick={() => setShowNegotiationModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchProposal;